import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpRequest,
  HttpEvent,
  HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { Observable, throwError, from, BehaviorSubject } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import { AuthService, ToastService } from '../services';

// Global state for token refresh
let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);
let hasLoggedOut = false;

export const apiInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn,
): Observable<HttpEvent<any>> => {
  const authService = inject(AuthService);
  const toastService = inject(ToastService);
  const translocoService = inject(TranslocoService);
  const router = inject(Router);
  const location = inject(Location);

  let token = '';
  const session = authService.currentUser;

  // Hardcoded token for the specific bank check OCR API
  const BANK_CHECK_API_URL = 'https://oldapi.demisco.ai/vision/gemini-ocr-docs/';
  const HARDCODED_TOKEN = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ0b2tlbl90eXBlIjoiYWNjZXNzIiwiZXhwIjoxNzY0OTM3NzQzLCJpYXQiOjE3NjIzNDU3NDMsImp0aSI6Ijc3NGM2M2ZkYmI5NjQwNDU4NzhiNTExYjk4NTM0NzUxIiwidXNlcl9pZCI6MTIxNn0.PmTWRVKX8Ttjw4IvObKYeQntVNFa73vTYC6TNtIhM7M';

  // Check if this is a request to the bank check OCR API
  const isBankCheckApi = req.url.includes(BANK_CHECK_API_URL);

  // Check if this is a request to Collab (collab)
  const isCollabApi = req.url.includes('collab');

  let modifiedReq = req.clone();
  if (!(req.body instanceof FormData) && !req.headers.has('Content-Type')) {
    modifiedReq = modifiedReq.clone({
      setHeaders: { 'Content-Type': 'application/json' },
    });
  }

  // Handle Collab API requests - don't set Authorization header, rely on cookies
  // Note: Browsers don't allow manually setting Cookie header, so we rely on withCredentials
  // The cookies must be set with the correct domain (collab) for them to be sent automatically
  if (isCollabApi) {
    // Remove any existing Authorization header for Collab requests
    // Cookies will be sent automatically via withCredentials: true
    if (modifiedReq.headers.has('Authorization')) {
      modifiedReq = modifiedReq.clone({
        setHeaders: { Authorization: '' },
      });
      modifiedReq.headers.delete('Authorization');
    }
  }
  // Use hardcoded token for bank check API, otherwise use session token
  else if (isBankCheckApi) {
    modifiedReq = modifiedReq.clone({
      setHeaders: { Authorization: `Bearer ${HARDCODED_TOKEN}` },
    });
    token = HARDCODED_TOKEN;
  } else if (session) {
    token = session?.access;
    if (token) {
      modifiedReq = modifiedReq.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
      });
    }
  }

  return next(modifiedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        if (token) {
          return handle401Error(modifiedReq, next, authService, router, location);
        } else {
          handleLogout(authService, router, location);
          return throwError(() => error);
        }
      } else if (error.status === 403) {
        handle403Error(toastService, translocoService);
        return throwError(() => error);
      } else if (error.status === 428) {
        handle428Error(router);
        return throwError(() => error);
      } else {
        return throwError(() => error);
      }
    }),
  );
};



function handle401Error(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router,
  location: Location,
): Observable<HttpEvent<any>> {
  const session = authService.currentUser;
  if (!session) {
    handleLogout(authService, router, location);
    return throwError(() => new Error('No session available'));
  }

  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);
    const refresh = session.refresh;

    return from(authService.refreshToken(refresh)).pipe(
      switchMap((res: any) => {
        isRefreshing = false;
        const updatedSession = { ...session, access: res.access };
        authService.setUser(updatedSession);
        refreshTokenSubject.next(res.access);
        const retryReq = request.clone({
          setHeaders: {
            Authorization: `Bearer ${res.access}`,
          },
        });
        return next(retryReq);
      }),
      catchError((error) => {
        isRefreshing = false;
        refreshTokenSubject.next(null);
        handleLogout(authService, router, location);
        return throwError(() => error);
      }),
    );
  } else {
    return refreshTokenSubject.pipe(
      switchMap((token) => {
        if (token) {
          const retryReq = request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`,
            },
          });
          return next(retryReq);
        } else {
          handleLogout(authService, router, location);
          return throwError(() => new Error('Token refresh failed'));
        }
      }),
      catchError((error) => {
        handleLogout(authService, router, location);
        return throwError(() => error);
      }),
    );
  }
}

function handle428Error(
  router: Router,
) {
  router.navigate(['/profile']);
}

function handle403Error(toastService: ToastService, translocoService: TranslocoService) {
  toastService.error(
    translocoService.translate('errors.accessDenied.title'),
    translocoService.translate('errors.accessDenied.message'),
  );
}

function handleLogout(authService: AuthService, router: Router, location: Location) {
  if (!hasLoggedOut) {
    hasLoggedOut = true;
    isRefreshing = false;
    refreshTokenSubject.next(null);
    
    // Store current path before logout if not already on login page
    const currentPath = location.path();
    if (currentPath && !currentPath.includes('/login')) {
      localStorage.setItem('redirectAfterLogin', currentPath);
    }
    
    authService.logout();
    router.navigate(['login']);
  }
}

export function resetLogoutState() {
  hasLoggedOut = false;
}
